/**
 * #2934 A11Y-05 (report ref G-05) - WCAG 4.1.2 Name, Role, Value / 2.4.3 Focus Order.
 *
 * react-aria-carousel marks every item outside the active page as `inert` + `aria-hidden`.
 * With fractional `itemsPerPage` that includes the partially visible "peek" tiles, which made
 * them unclickable for everyone, so the carousel previously stripped `inert` from all items
 * unconditionally via a MutationObserver.
 *
 * Stripping it wholesale left genuinely off-screen slides keyboard-focusable while still
 * `aria-hidden`: a keyboard user tabbed into content they could not see and which was hidden
 * from assistive technology. axe reports it as `aria-hidden-focus` - 40 nodes on the homepage,
 * 108 on the books landing page.
 *
 * Neither extreme is correct. Visibility decides: any slide with part of itself inside the
 * scroller stays interactive; a slide entirely outside it becomes `inert` and `aria-hidden`
 * together, so the two can never disagree.
 */

/** True when any part of the child lies within the scroller's horizontal bounds. */
const isWithin = (child: Element, scroller: Element) => {
  const bounds = scroller.getBoundingClientRect();
  const box = child.getBoundingClientRect();

  return box.right > bounds.left && box.left < bounds.right;
};

export const syncCarouselInert = (scroller: HTMLElement) => {
  const visible = new WeakSet<Element>();
  let attributes: MutationObserver | null = null;

  const apply = () => {
    // Our own writes would otherwise re-trigger the attribute observer below.
    attributes?.disconnect();

    for (const child of scroller.children) {
      const offScreen = !visible.has(child);

      child.toggleAttribute('inert', offScreen);

      if (offScreen) {
        child.setAttribute('aria-hidden', 'true');
      } else {
        child.removeAttribute('aria-hidden');
      }
    }

    attributes?.observe(scroller, {
      attributeFilter: ['inert', 'aria-hidden'],
      attributes: true,
      subtree: true,
    });
  };

  /**
   * IntersectionObserver reports asynchronously, so measure synchronously first. The audit
   * specifically calls out slides being wrong on initial render, not only after a transition.
   */
  const seed = () => {
    for (const child of scroller.children) {
      if (isWithin(child, scroller)) {
        visible.add(child);
      } else {
        visible.delete(child);
      }
    }
  };

  const intersections = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          visible.add(entry.target);
        } else {
          visible.delete(entry.target);
        }
      }

      apply();
    },
    // threshold 0: any sliver of a peek tile inside the scroller counts as visible.
    { root: scroller, threshold: 0 },
  );

  const observeChildren = () => {
    for (const child of scroller.children) {
      intersections.observe(child);
    }
  };

  // Items are re-rendered as the carousel pages, so new children need observing and the whole
  // set re-measuring.
  const children = new MutationObserver(() => {
    intersections.disconnect();
    observeChildren();
    seed();
    apply();
  });

  attributes = new MutationObserver(apply);

  seed();
  observeChildren();
  apply();
  children.observe(scroller, { childList: true });

  return () => {
    intersections.disconnect();
    children.disconnect();
    attributes?.disconnect();
  };
};
